import type { Session } from '@supabase/supabase-js';
import type {
  AuthSession,
  MonthFocus,
  Plan,
  Profile,
  Space,
  SpaceInvite,
  SpaceMember,
  UserPreferences,
} from '@/domain/types';
import { getDb, loadSession, saveSession, setMeta, getMeta } from '@/storage/db';
import { enqueueSync } from '@/sync/engine';
import { config } from '@/app/config';
import { getSupabase } from '@/supabase/client';
import {
  buildDemoSeed,
  DEMO_ALEX_ID,
  DEMO_MIA_ID,
  DEMO_SPACE_ID,
} from '@/services/demoSeed';
import { createId, hashInviteCode, generateInviteCode, nowIso } from '@/domain/helpers';
import { mapSpaceErrorMessage } from '@/domain/spaceErrors';
import { MAX_SPACE_MEMBERS } from '@/domain/types';

async function hydrateAfterSpaceChange(userId: string): Promise<void> {
  const { hydrateFromServer } = await import('@/sync/supabaseSync');
  await hydrateFromServer(userId);
}

/** Drop local space-scoped rows so a new membership does not mix with an abandoned space. */
async function clearLocalSpaceScopedData(): Promise<void> {
  const db = getDb();
  await db.transaction(
    'rw',
    [db.spaces, db.spaceMembers, db.spaceInvites, db.plans, db.monthFocus],
    async () => {
      await db.plans.clear();
      await db.monthFocus.clear();
      await db.spaces.clear();
      await db.spaceMembers.clear();
      await db.spaceInvites.clear();
    },
  );
  await setMeta('current_space_id', null);
  await setMeta('last_invite_code', null);
}

export async function ensureDemoData(locale: 'en' | 'ru' = 'en'): Promise<void> {
  const seeded = await getMeta<boolean>('demo_seeded');
  if (seeded) return;
  const seed = await buildDemoSeed(locale);
  const db = getDb();
  await db.transaction(
    'rw',
    [
      db.profiles,
      db.spaces,
      db.spaceMembers,
      db.spaceInvites,
      db.plans,
      db.monthFocus,
      db.preferences,
    ],
    async () => {
      await db.profiles.bulkPut(seed.profiles);
      await db.spaces.put(seed.space);
      await db.spaceMembers.bulkPut(seed.members);
      await db.spaceInvites.put(seed.invite);
      await db.plans.bulkPut(seed.plans);
      await db.monthFocus.bulkPut(seed.monthFocus);
      await db.preferences.bulkPut(seed.preferences);
    },
  );
  await setMeta('demo_seeded', true);
}

export async function demoLogin(who: 'alex' | 'mia'): Promise<AuthSession> {
  await ensureDemoData();
  const userId = who === 'alex' ? DEMO_ALEX_ID : DEMO_MIA_ID;
  const email = who === 'alex' ? 'alex@twyme.demo' : 'mia@twyme.demo';
  const session: AuthSession = {
    user: { id: userId, email, emailConfirmed: true },
    accessToken: `demo_${userId}`,
    refreshToken: `demo_refresh_${userId}`,
    expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000,
  };
  await saveSession(session);
  await setMeta('current_space_id', DEMO_SPACE_ID);
  return session;
}

export async function getCurrentSession(): Promise<AuthSession | null> {
  const session = await loadSession();
  if (!session) return null;
  if (session.expiresAt < Date.now()) {
    await saveSession(null);
    return null;
  }
  return session;
}

export async function logoutLocal(): Promise<void> {
  await saveSession(null);
  if (config.mode === 'production') {
    const supabase = getSupabase();
    if (supabase) {
      await supabase.auth.signOut();
    }
  }
}

export function mapSupabaseSession(session: Session): AuthSession {
  return {
    user: {
      id: session.user.id,
      email: session.user.email ?? '',
      emailConfirmed: Boolean(session.user.email_confirmed_at),
    },
    accessToken: session.access_token,
    refreshToken: session.refresh_token ?? '',
    expiresAt: (session.expires_at ?? Math.floor(Date.now() / 1000) + 3600) * 1000,
  };
}

export async function persistSupabaseSession(session: Session): Promise<AuthSession> {
  const authSession = mapSupabaseSession(session);
  await saveSession(authSession);
  return authSession;
}

export class PlansRepository {
  async listForSpace(spaceId: string): Promise<Plan[]> {
    return getDb().plans.where('spaceId').equals(spaceId).toArray();
  }

  async get(id: string): Promise<Plan | undefined> {
    return getDb().plans.get(id);
  }

  async upsert(plan: Plan, enqueue = true): Promise<Plan> {
    await getDb().plans.put(plan);
    if (enqueue && config.mode === 'production') {
      const session = await loadSession();
      if (!session) return plan;
      // Space members may sync partner plans after migration 011; still never
      // enqueue redacted Busy stubs (would overwrite the real title).
      if (plan.ownerId !== session.user.id && plan.title === 'Busy') {
        return plan;
      }
      await enqueueSync({
        entityType: 'plan',
        entityId: plan.id,
        action: plan.deletedAt ? 'delete' : 'upsert',
        payload: plan,
      });
    }
    return plan;
  }

  async softDelete(id: string): Promise<Plan | null> {
    const existing = await this.get(id);
    if (!existing) return null;
    const ts = nowIso();
    const updated: Plan = {
      ...existing,
      deletedAt: ts,
      updatedAt: ts,
      clientUpdatedAt: ts,
    };
    return this.upsert(updated);
  }
}

export class SpaceRepository {
  async getSpace(spaceId: string): Promise<Space | undefined> {
    return getDb().spaces.get(spaceId);
  }

  async getMembers(spaceId: string): Promise<SpaceMember[]> {
    return getDb()
      .spaceMembers.where('spaceId')
      .equals(spaceId)
      .filter((m) => m.memberStatus === 'active')
      .toArray();
  }

  async getProfilesForSpace(spaceId: string): Promise<Profile[]> {
    const members = await this.getMembers(spaceId);
    const ids = members.map((m) => m.userId);
    return getDb().profiles.bulkGet(ids).then((rows) => rows.filter(Boolean) as Profile[]);
  }

  async getInvite(spaceId: string): Promise<SpaceInvite | undefined> {
    return getDb()
      .spaceInvites.where('spaceId')
      .equals(spaceId)
      .filter((i) => i.status === 'pending')
      .first();
  }

  async createSpace(createdBy: string, name = 'twyme-space'): Promise<{ space: Space; inviteCode: string }> {
    const members = await getDb().spaceMembers.where('userId').equals(createdBy).toArray();
    if (members.some((m) => m.memberStatus === 'active')) {
      throw new Error('User already in a space');
    }

    const code = generateInviteCode();
    const codeHash = await hashInviteCode(code);

    if (config.mode === 'production') {
      const supabase = getSupabase();
      if (!supabase) throw new Error('Supabase unavailable');
      const { data, error } = await supabase.rpc('create_space_with_invite', {
        p_code_hash: codeHash,
        p_name: name,
      });
      if (error) throw new Error(mapSpaceErrorMessage(error.message));
      const row = Array.isArray(data) ? data[0] : data;
      const spaceId = String((row as { space_id: string }).space_id);
      const inviteId = String((row as { invite_id: string }).invite_id);
      await setMeta('last_invite_code', code);
      await setMeta('current_space_id', spaceId);
      try {
        await hydrateAfterSpaceChange(createdBy);
      } catch (hydrateErr) {
        const msg = hydrateErr instanceof Error ? hydrateErr.message : String(hydrateErr);
        if (/permission denied|42501/i.test(msg)) {
          throw new Error(
            'Database grants missing. Apply migration 20260328000004_grants_and_viewer.sql in the Supabase SQL Editor, then retry.',
          );
        }
        // Seed local row so invite UI can continue if hydrate partially failed
        const ts = nowIso();
        const db = getDb();
        await db.spaces.put({
          id: spaceId,
          name,
          createdBy,
          createdAt: ts,
          updatedAt: ts,
        });
        await db.spaceMembers.put({
          spaceId,
          userId: createdBy,
          joinedAt: ts,
          memberStatus: 'active',
        });
        await db.spaceInvites.put({
          id: inviteId,
          spaceId,
          codeHash,
          codeHint: code,
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'pending',
          createdBy,
          usedAt: null,
          usedBy: null,
          createdAt: ts,
        });
      }
      const space = await this.getSpace(spaceId);
      if (!space) throw new Error('Space missing after create');
      // Keep plaintext locally for display until partner joins (never in DB)
      const invite = await this.getInvite(spaceId);
      if (invite) {
        await getDb().spaceInvites.put({ ...invite, id: inviteId, codeHint: code });
      }
      return { space, inviteCode: code };
    }

    const ts = nowIso();
    const space: Space = {
      id: createId(),
      name,
      createdBy,
      createdAt: ts,
      updatedAt: ts,
    };
    const member: SpaceMember = {
      spaceId: space.id,
      userId: createdBy,
      joinedAt: ts,
      memberStatus: 'active',
    };
    const invite: SpaceInvite = {
      id: createId(),
      spaceId: space.id,
      codeHash,
      codeHint: code,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
      createdBy,
      usedAt: null,
      usedBy: null,
      createdAt: ts,
    };
    const db = getDb();
    await db.transaction('rw', [db.spaces, db.spaceMembers, db.spaceInvites], async () => {
      await db.spaces.put(space);
      await db.spaceMembers.put(member);
      await db.spaceInvites.put(invite);
    });
    await setMeta('current_space_id', space.id);
    await setMeta('last_invite_code', code);
    return { space, inviteCode: code };
  }

  async abandonEmptySoloSpace(): Promise<string | null> {
    if (config.mode !== 'production') {
      return null;
    }
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase unavailable');
    const { data, error } = await supabase.rpc('abandon_empty_solo_space');
    if (error) throw new Error(mapSpaceErrorMessage(error.message));
    await clearLocalSpaceScopedData();
    return data ? String(data) : null;
  }

  async joinByCode(userId: string, code: string, options?: { abandonSoloIfNeeded?: boolean }): Promise<Space> {
    const hash = await hashInviteCode(code.trim().toUpperCase());
    const abandonSoloIfNeeded = options?.abandonSoloIfNeeded ?? true;

    if (config.mode === 'production') {
      const supabase = getSupabase();
      if (!supabase) throw new Error('Supabase unavailable');

      const attemptJoin = async () => {
        const { data: spaceId, error } = await supabase.rpc('join_space_with_invite', {
          p_code_hash: hash,
        });
        if (error) throw new Error(mapSpaceErrorMessage(error.message));
        return String(spaceId);
      };

      let spaceId: string;
      try {
        spaceId = await attemptJoin();
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!abandonSoloIfNeeded || !/already in a space/i.test(msg)) throw err;
        try {
          await this.abandonEmptySoloSpace();
        } catch (leaveErr) {
          const leaveMsg = leaveErr instanceof Error ? leaveErr.message : String(leaveErr);
          if (/has plans|month focus|not a solo|partner/i.test(leaveMsg)) {
            throw new Error(
              'Already in a space. Leave or finish your current space before joining another invite.',
            );
          }
          throw leaveErr;
        }
        spaceId = await attemptJoin();
      }

      await clearLocalSpaceScopedData();
      await setMeta('current_space_id', spaceId);
      await setMeta('last_invite_code', null);
      await hydrateAfterSpaceChange(userId);
      const space = await this.getSpace(spaceId);
      if (!space) throw new Error('Space missing after join');
      return space;
    }

    const invite = await getDb().spaceInvites.where('codeHash').equals(hash).first();
    if (!invite) throw new Error('Invite not found');
    if (invite.status !== 'pending') throw new Error('Invite already used');
    if (new Date(invite.expiresAt).getTime() < Date.now()) {
      await getDb().spaceInvites.update(invite.id, { status: 'expired' });
      throw new Error('Invite expired');
    }
    const myActive = await getDb()
      .spaceMembers.where('userId')
      .equals(userId)
      .filter((m) => m.memberStatus === 'active')
      .first();
    if (myActive) {
      if (!abandonSoloIfNeeded) throw new Error('Already in a space');
      const others = await this.getMembers(myActive.spaceId);
      if (others.length > 1) throw new Error('Already in a space');
      const plans = await getDb()
        .plans.where('spaceId')
        .equals(myActive.spaceId)
        .filter((p) => !p.deletedAt)
        .count();
      if (plans > 0) {
        throw new Error(
          'Already in a space. Leave or finish your current space before joining another invite.',
        );
      }
      await clearLocalSpaceScopedData();
    }
    const activeMembers = await this.getMembers(invite.spaceId);
    if (activeMembers.length >= MAX_SPACE_MEMBERS) {
      throw new Error('Space is full');
    }
    if (activeMembers.some((m) => m.userId === userId)) {
      throw new Error('Already in a space');
    }
    const ts = nowIso();
    const member: SpaceMember = {
      spaceId: invite.spaceId,
      userId,
      joinedAt: ts,
      memberStatus: 'active',
    };
    const db = getDb();
    await db.transaction('rw', [db.spaceMembers, db.spaceInvites], async () => {
      const again = await this.getMembers(invite.spaceId);
      if (again.length >= MAX_SPACE_MEMBERS) throw new Error('Space is full');
      await db.spaceMembers.put(member);
      await db.spaceInvites.put({
        ...invite,
        status: 'used',
        usedAt: ts,
        usedBy: userId,
      });
    });
    await setMeta('current_space_id', invite.spaceId);
    const space = await this.getSpace(invite.spaceId);
    if (!space) throw new Error('Space missing');
    return space;
  }

  async regenerateInvite(userId: string, spaceId: string): Promise<string> {
    const members = await this.getMembers(spaceId);
    if (!members.some((m) => m.userId === userId)) {
      throw new Error('Not a space member');
    }
    if (members.length >= MAX_SPACE_MEMBERS) {
      throw new Error('Space is full');
    }
    const code = generateInviteCode();
    const codeHash = await hashInviteCode(code);

    if (config.mode === 'production') {
      const supabase = getSupabase();
      if (!supabase) throw new Error('Supabase unavailable');
      const { data: inviteId, error } = await supabase.rpc('create_invite_for_space', {
        p_space_id: spaceId,
        p_code_hash: codeHash,
      });
      if (error) throw new Error(mapSpaceErrorMessage(error.message));
      await setMeta('last_invite_code', code);
      await hydrateAfterSpaceChange(userId);
      const invite = await this.getInvite(spaceId);
      if (invite) {
        await getDb().spaceInvites.put({
          ...invite,
          id: String(inviteId),
          codeHint: code,
        });
      }
      return code;
    }

    const ts = nowIso();
    const db = getDb();
    const pending = await db.spaceInvites
      .where('spaceId')
      .equals(spaceId)
      .filter((i) => i.status === 'pending')
      .toArray();
    for (const inv of pending) {
      await db.spaceInvites.put({ ...inv, status: 'revoked' });
    }
    const invite: SpaceInvite = {
      id: createId(),
      spaceId,
      codeHash,
      codeHint: code,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'pending',
      createdBy: userId,
      usedAt: null,
      usedBy: null,
      createdAt: ts,
    };
    await db.spaceInvites.put(invite);
    await setMeta('last_invite_code', code);
    return code;
  }
}

export class ProfileRepository {
  async get(id: string): Promise<Profile | undefined> {
    return getDb().profiles.get(id);
  }

  async upsert(profile: Profile): Promise<Profile> {
    await getDb().profiles.put(profile);
    if (config.mode === 'production') {
      await enqueueSync({
        entityType: 'profile',
        entityId: profile.id,
        action: 'upsert',
        payload: profile,
      });
    }
    return profile;
  }
}

export class PreferencesRepository {
  async get(userId: string): Promise<UserPreferences | undefined> {
    return getDb().preferences.get(userId);
  }

  async upsert(prefs: UserPreferences): Promise<UserPreferences> {
    await getDb().preferences.put(prefs);
    if (config.mode === 'production') {
      await enqueueSync({
        entityType: 'preferences',
        entityId: prefs.userId,
        action: 'upsert',
        payload: prefs,
      });
    }
    return prefs;
  }
}

export class MonthFocusRepository {
  async list(spaceId: string): Promise<MonthFocus[]> {
    return getDb().monthFocus.where('spaceId').equals(spaceId).toArray();
  }

  async upsert(focus: MonthFocus): Promise<MonthFocus> {
    await getDb().monthFocus.put(focus);
    if (config.mode === 'production') {
      await enqueueSync({
        entityType: 'month_focus',
        entityId: focus.id,
        action: focus.deletedAt ? 'delete' : 'upsert',
        payload: focus,
      });
    }
    return focus;
  }
}

export const plansRepo = new PlansRepository();
export const spaceRepo = new SpaceRepository();
export const profileRepo = new ProfileRepository();
export const preferencesRepo = new PreferencesRepository();
export const monthFocusRepo = new MonthFocusRepository();
